import { getIncomers, type Node } from '@xyflow/react';
import {
  getDescriptionsFromImageNodes,
  getFilesFromFileNodes,
  getTextFromFileNodes,
  getImagesFromImageNodes,
  getImagesFromUploadNodes,
  getVideosFromVideoNodes,
  getVideosFromUploadNodes,
  getTextFromTextNodes,
  getTranscriptionFromAudioNodes,
  getAudiosFromUploadNodes,
  getTweetContentFromTweetNodes,
} from '@/lib/xyflow';
import { handleError } from '@/lib/error/handle';
import { ensureProjectSavedOrShowRefreshModal } from '@/lib/project-save';

export type CreateTextHandleGenerateParams = {
  t: (key: string, vars?: Record<string, unknown>) => string;
  id: string;
  type: string;
  data: any;
  modelId: string;
  getNodes: () => Node[];
  getEdges: () => any[];
  updateNodeData: (id: string, partial: any) => void;
  append: (message: { role: 'user'; content: string; experimental_attachments?: Array<{ url: string; contentType: string; name?: string }> }) => void;
  setMessages: (messages: any[]) => void;
  analyticsTrack?: (category: string, group: string, action: string, payload: any) => void;
};

export const createTextHandleGenerate = (params: CreateTextHandleGenerateParams) => {
  const { t, id, type, data, modelId, getNodes, getEdges, updateNodeData, append, setMessages, analyticsTrack } = params;

  const handleGenerate = async () => {
    try {
      // ========================== 生成前强制保存并等待（严格） ==========================
      // 保存失败/超时：不继续生成，并提示用户刷新
      const ok = await ensureProjectSavedOrShowRefreshModal(10000);
      if (!ok) return;

      // 清理之前的生成结果与失败状态。同时 bump `updatedAt` — 让自动运行
      // 调度器立刻看到"已经开始"的信号（hasActivity=true），避免 LLM 流式
      // 响应较慢时被 grace period 误判为"未启动"。
      // - `status` / `error` 清空：避免上次失败的残留状态在新一轮被误判
      //   为本轮失败（status='failed' + 新 updatedAt）。
      // - `generated` 清空：让 detectState 用 `textIsFresh` 判断完成时，
      //   onFinish 写入的新文本与基线（undefined / 旧值）严格不同。
      updateNodeData(id, {
        generated: undefined,
        status: undefined,
        error: undefined,
        updatedAt: new Date().toISOString(),
      });

      // 上游输入
      const incomers = getIncomers({ id }, getNodes(), getEdges());
      const textPrompts = getTextFromTextNodes(incomers);
      const fileTexts = getTextFromFileNodes(incomers);
      const audioPrompts = getTranscriptionFromAudioNodes(incomers);
      // 同时收集来自图像节点与上传节点(图片类型)的图片
      const imagesFromImageNodes = getImagesFromImageNodes(incomers);
      const imagesFromUploadNodes = getImagesFromUploadNodes(incomers);
      const images = [...imagesFromImageNodes, ...imagesFromUploadNodes];
      const videosFromVideoNodes = getVideosFromVideoNodes(incomers);
      const videosFromUploadNodes = getVideosFromUploadNodes(incomers);
      const videos = [...videosFromVideoNodes, ...videosFromUploadNodes];
      // 收集来自上传节点(音频类型)的音频
      const audiosFromUploadNodes = getAudiosFromUploadNodes(incomers);
      const imageDescriptions = getDescriptionsFromImageNodes(incomers);
      const tweetContent = getTweetContentFromTweetNodes(incomers);
      const files = getFilesFromFileNodes(incomers);

      // 合并文本节点的文本和文件节点的文本
      const allTextPrompts = [...textPrompts, ...fileTexts];

      if (!allTextPrompts.length && !audioPrompts.length && !audiosFromUploadNodes.length && !data?.instructions) {
        const msg = t('No prompts found');
        handleError(t('Error generating text'), msg);
        // 同步失败也要写入 status，让自动运行调度器能立刻看到失败信号，
        // 避免下游被无谓地等 2 分钟超时。
        try {
          updateNodeData(id, {
            status: 'failed',
            error: msg,
            updatedAt: new Date().toISOString(),
          });
        } catch {}
        return;
      }

      // 组装为标准对象
      const firstVideoUrl = (() => {
        if (Array.isArray(videos) && videos.length) {
          const urls = videos.map((v: any) => (typeof v === 'string' ? v : v?.url)).filter(Boolean) as string[];
          return urls[0] ?? '';
        }
        return '';
      })();
      const firstImageUrl = (() => {
        if (Array.isArray(images) && images.length) {
          const urls = images
            .map((img: any) => (typeof img === 'string' ? img : img?.url))
            .filter(Boolean) as string[];
          return urls[0] ?? '';
        }
        return '';
      })();
      const payloadObject = {
        instructions: String(data?.instructions ?? ''),
        textPrompt: allTextPrompts.join('\n'),
        videoUrls: firstVideoUrl,
        firstImageUrl: firstImageUrl,
      };
      const payloadJson = JSON.stringify(payloadObject, null, 2);

      // 触发生成（useChat）
      setMessages([]);
      append({
        role: 'user',
        content: payloadJson,
        experimental_attachments: [
          ...images.map((image) => ({ url: image.url, contentType: image.type })),
          ...files.map((file) => ({ url: file.url, contentType: file.type, name: file.name })),
          ...audiosFromUploadNodes.map((audio) => ({ url: audio.url, contentType: audio.type, name: audio.name })),
        ],
      });
    } catch (error) {
      handleError(t('Error generating text'), error);
      // 写入 status 让自动运行调度器立即识别失败（避免 2 分钟超时）。
      try {
        const message = (error instanceof Error ? error.message : String(error ?? '')) || 'Text generation failed';
        updateNodeData(id, {
          status: 'failed',
          error: message,
          updatedAt: new Date().toISOString(),
        });
      } catch {}
    }
  };

  return handleGenerate;
};


